/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganizationService } from '@/features/Organization/services/organizationService';
import { SubscriptionService } from '@/features/Subscriptions/services/SubscriptionService';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useTranslation } from 'react-i18next';
import type { Plan, Invoice } from '@/features/Subscriptions/types/subscription-types';
import { analyticsService } from '@/shared/services/analytics';

interface OrganizationData {
  name: string;
  info: string;
  industry: string;
  size: string;
  stage: string;
  website: string;
}

export const useOrganizationSubmit = (
  selectedPlan: Plan | null,
  logoFile: File | null,
  _uploadLogo?: (file: File, orgId: string) => Promise<string | null> // Deprecated, kept for backwards compatibility
) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string>('');

  // Ref para rastrear tempo de início do onboarding
  const onboardingStartTime = useRef<number>(Date.now());

  // Modais
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState<Invoice | null>(null);
  const [isTrialSuccessModalOpen, setIsTrialSuccessModalOpen] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState<string>('');
  const [trialCredits, setTrialCredits] = useState<number | undefined>(undefined);

  const handleRequestAccess = async () => {
    console.log('[Onboarding] User requested waitlist access - logging out');
    await signOut();
    navigate('/login');
  };

  const onSubmit = async (values: OrganizationData) => {
    setIsSubmitting(true);
    setError(null);
    setPlanError('');

    // Timeout de segurança para garantir que o spinner sempre desapareça
    const timeoutId = setTimeout(() => {
      console.error('[Onboarding] TIMEOUT: Operação demorou mais de 30 segundos');
      setIsSubmitting(false);
      setError(t("auth.onboarding.errors.timeout", {
        defaultValue: "A operação está demorando mais do que o esperado. Por favor, verifique sua conexão e tente novamente."
      }));
    }, 30000); // 30 segundos

    try {
      // Validar se um plano foi selecionado
      if (!selectedPlan) {
        clearTimeout(timeoutId);
        setPlanError(i18n.language.startsWith('pt') ? '*Por favor, selecione um plano para continuar' : '*Please select a plan to continue');
        setIsSubmitting(false);
        return;
      }

      // Primeiro, criar a organização (com logo se houver)
      console.log('[Onboarding] Criando organização com os dados:', {
        name: values.name,
        info: values.info,
        industry: values.industry,
        size: values.size,
        stage: values.stage,
        website: values.website,
        from_onboarding: true,
        plan_id: selectedPlan.id,
        hasLogo: !!logoFile,
      });

      console.log('[Onboarding] Iniciando requisição createOrganization...');
      const response = await OrganizationService.createOrganization({
        name: values.name,
        info: values.info,
        industry: values.industry,
        size: values.size,
        stage: values.stage,
        website: values.website,
        logo: logoFile || undefined,
        from_onboarding: true,
        plan_id: selectedPlan.id,
      } as any);

      console.log('[Onboarding] Response da criação de organização:', response);

      // OrganizationService não lança exceção, retorna { success: false }
      if (!response.success) {
        clearTimeout(timeoutId);
        console.error('[Onboarding] Erro ao criar organização:', response.message);

        // Verificar se é erro de waitlist
        const errorMessage = response.message || '';
        const fullErrorText = errorMessage.toLowerCase();

        console.log('[Onboarding] Checking for waitlist error in:', fullErrorText);

        const isWaitlistError = fullErrorText.includes('lista de trial') ||
          fullErrorText.includes('waitlist') ||
          fullErrorText.includes('não está inscrito') ||
          fullErrorText.includes('não autorizado') ||
          fullErrorText.includes('status 403');

        if (isWaitlistError) {
          console.log('[Onboarding] Waitlist error detected - showing validator modal');
          setIsSubmitting(false);
          setIsWaitlistModalOpen(true);
          return;
        }

        // Outros erros
        setError(response.message || t("auth.onboarding.errors.createFailed"));
        setIsSubmitting(false);
        return;
      }

      if (response.success && response.data) {
        // Subscription agora é criada automaticamente pelo backend junto com a organização
        console.log('[Onboarding] Organização e subscription criadas com sucesso');

        // Se backend retornou novos tokens com organization_id, salvar no localStorage
        const tokens = (response.data as any)?.tokens ||
          (response.data as any)?.enhancedToken ||
          (response as any)?.tokens;

        if (tokens?.accessToken && tokens?.refreshToken) {
          console.log('[Onboarding] Salvando tokens atualizados retornados pelo backend');
          localStorage.setItem('access_token', tokens.accessToken);
          localStorage.setItem('refresh_token', tokens.refreshToken);
        } else {
          // Tokens já foram sincronizados no OAuth callback, não é necessário refresh
          console.log('[Onboarding] Tokens não vieram na resposta, mas já foram sincronizados anteriormente');
        }

        // Verificar status de pagamento para determinar próximo passo
        try {
          const paymentStatus = await SubscriptionService.paymentCheck();

          console.log('[Onboarding] Payment check response:', JSON.stringify(paymentStatus, null, 2));

          const paymentData = paymentStatus.data;

          // Decisão baseada no response do payment-check
          if (paymentData.access_level === 'full' && !paymentData.requires_action) {
            // Cenário A: TRIAL - Mostrar modal de sucesso
            clearTimeout(timeoutId);
            console.log('[Onboarding] Trial plan - showing success modal');


            // Extrair dados do trial
            const trialEnd = paymentData.subscription?.trial_end_date || paymentData.subscription?.due_date || '';
            // Credits podem vir do backend futuramente, por enquanto não exibimos
            const credits = undefined; // TODO: adicionar credits quando disponível no backend

            setTrialEndDate(trialEnd);
            setTrialCredits(credits);
            setIsTrialSuccessModalOpen(true);

            // Track onboarding completion
            const durationSeconds = analyticsService.getDurationSeconds(onboardingStartTime.current);
            analyticsService.trackOnboardingCompleted({
              organization_name: values.name,
              duration_seconds: durationSeconds
            });

            setIsSubmitting(false);
          } else if (paymentData.requires_action && paymentData.action_type === 'complete_payment' && paymentData.invoice) {
            // Cenário B: PLANO PAGO - Mostrar modal de pagamento
            clearTimeout(timeoutId);
            console.log('[Onboarding] Paid plan - showing payment modal');

            setPendingInvoice(paymentData.invoice);
            setIsPaymentModalOpen(true);
            setIsSubmitting(false); // Liberar interface para modal
          } else {
            // Fallback: redirecionar para dashboard
            clearTimeout(timeoutId);
            console.log('[Onboarding] Fallback - redirecting to dashboard (paymentStatus not matching expected cases)');

            if (typeof window !== 'undefined') {
              sessionStorage.removeItem('onboarding_profile');
              sessionStorage.removeItem('onboarding_survey');
              sessionStorage.removeItem('onboarding_organization');
              sessionStorage.removeItem('onboarding_avatar_preview');
              sessionStorage.removeItem('onboarding_logo_preview');
            }

            setTimeout(() => {
              window.location.href = '/';
            }, 1000);
          }
        } catch (paymentCheckError: any) {
          clearTimeout(timeoutId);
          console.error('[Onboarding] Erro ao verificar status de pagamento:', paymentCheckError);
          // NÃO prosseguir automaticamente - mostrar erro ao usuário
          setIsSubmitting(false);
          setError(
            paymentCheckError?.message ||
            t("auth.onboarding.errors.paymentCheckFailed", {
              defaultValue: "Não foi possível verificar o status do pagamento. Por favor, tente novamente ou entre em contato com o suporte."
            })
          );
          return; // Interrompe o fluxo
        }
      } else {
        clearTimeout(timeoutId);
        setError(response.message || t("auth.onboarding.errors.createFailed"));
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      console.error('[Onboarding] Erro no fluxo de onboarding:', err);

      // Verificar se é erro de waitlist
      const errorMessage = err?.message || '';
      const errorData = err?.data?.message || err?.data?.error || JSON.stringify(err?.data || {});
      const errorResponse = err?.response?.data?.message || err?.response?.data?.error || '';

      const fullErrorText = `${errorMessage} ${errorData} ${errorResponse}`.toLowerCase();

      const isWaitlistError = fullErrorText.includes('lista de trial') ||
        fullErrorText.includes('waitlist') ||
        fullErrorText.includes('não está inscrito') ||
        fullErrorText.includes('não autorizado para plano trial');

      if (isWaitlistError) {
        // Mostrar modal de waitlist
        console.log('[Onboarding] Waitlist error detected in organization creation - showing validator modal');
        setIsWaitlistModalOpen(true);
        return;
      }

      // Outros erros
      setError(err.message || t("auth.onboarding.errors.generic"));
    } finally {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  return {
    onSubmit,
    isSubmitting,
    error,
    setError,
    planError,
    setPlanError,
    isWaitlistModalOpen,
    isPaymentModalOpen,
    pendingInvoice,
    isTrialSuccessModalOpen,
    trialEndDate,
    trialCredits,
    handleRequestAccess,
  };
};
